import React, { memo, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import styles from './ThirdStepOfProductDetails.module.scss'
import CommonFormFieldWrapper, { getFormInputErrors } from '../../../../../common/components/form-field-wrapper/CommonFormFieldWrapper'
import RadioButtonField from '../../../../../common/components/radio-button-field/RadioButtonField'
import ProductFormWrapper from '../components/ProductFormWrapper'
import CommonFileUploadInput from '../../../../../common/components/file-upload-input/CommonFileUploadInput'
import { ThirdStepOfSolarProductDetailsState, addNewProduct, editProduct, submitThirdStep } from '../../../../../stores/slices/solar-product-details-slice'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../../../../stores/store'
import { FileFromObjectUrl } from '../../../../../model/common/file-from-object-url'
import { toast } from 'react-toastify'
import { NumericFormat } from 'react-number-format'
import { hideLoader, showLoader } from '../../../../../stores/slices/loader-slice'

type Props = {}

const ThirdStepOfCreateProduct: React.FC<Props> = memo((props: Props) => {
    const dispatch = useDispatch<AppDispatch>();

    const thirdStepData = useSelector((state: RootState) => state.solarProductDetails.thirdStep)

    const productId = useSelector((state: RootState) => state.solarProductDetails.editingProductId)

    const error = useSelector((state: RootState) => state.solarProductDetails.error)

    const [selectedType, setSelectedType] = useState<boolean>(false)

    const initialImages = (thirdStepData : ThirdStepOfSolarProductDetailsState) => {
        return thirdStepData.existingImages?.map((image: FileFromObjectUrl) => image.name);
    }

    const {
        register,
        handleSubmit,
        setValue,
        control,
        watch,
        formState: { errors, isValid, isSubmitted },
    } = useForm({
        defaultValues: {
            ...thirdStepData,
            imageNames: initialImages(thirdStepData),
        },
        reValidateMode: 'onChange',
        mode: 'onTouched'
    })


    const submit = (data: any) => {
        dispatch(submitThirdStep(data))

        dispatch(showLoader())
        if (productId) {
            dispatch(editProduct()).then((result) => {
                if (result.meta.requestStatus === 'fulfilled') {
                    window.history.back()
                }
            }).finally(() => {
                dispatch(hideLoader())
            })
        } else {
            dispatch(addNewProduct()).then((result) => {
                if (result.meta.requestStatus === 'fulfilled') {
                    window.history.back()
                }
            }).finally(() => {
                dispatch(hideLoader())
            })
        }
    }

    const onFilesSelection = (file: File[]) => {
        const files: FileFromObjectUrl[] = []

        file.forEach((f: File) => {
            files.push({
                name: f.name,
                url: URL.createObjectURL(f),
            })
        })

        setValue('images', files, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
    }

    useEffect(() => {
        if (error) {
            toast.error(error)
        }
    }, [error])

    useEffect(() => {
        setValue('imageNames', initialImages(thirdStepData))
    }, [thirdStepData, setValue])

    useEffect(() => {
        const fieldsToSet = ['costForBungalow', 'costPerFloor', 'existingImages'];

        fieldsToSet.forEach((field) => {
            if (!thirdStepData[field as keyof ThirdStepOfSolarProductDetailsState]) {
                setValue(field as keyof ThirdStepOfSolarProductDetailsState, thirdStepData[field as keyof ThirdStepOfSolarProductDetailsState]);
                return;
            }

            setValue(field as keyof ThirdStepOfSolarProductDetailsState, thirdStepData[field as keyof ThirdStepOfSolarProductDetailsState], {
                shouldValidate: true,
            });
        });

        if (thirdStepData.images && thirdStepData.images.length > 0) {
            setValue('images', thirdStepData.images, {
                shouldValidate: true,
            })
        } else {
            setValue('images', [])
        }
    }, [thirdStepData, setValue])

    return (
        <ProductFormWrapper
            isValid={isValid}
            isSubmitted={isSubmitted}
            title='Product Details'
            onNext={handleSubmit(submit)}
            isLastStep
        >
            <CommonFormFieldWrapper
                title="Installation Cost"
                required={true}
                errors={[...getFormInputErrors(errors.costPerFloor?.message), ...getFormInputErrors(errors.costForBungalow?.message)]}
                borderNotVisible={true}>
                <RadioButtonField text="Bungalow"
                    imageUrl='https://www.thespruce.com/thmb/zFg7mPetp_7ZVqcScgJHbc7PSTg=/1500x0/filters:no_upscale():max_bytes(150000):strip_icc()/GettyImages-523075374-8d27b929fdfc4702aab5c73f821a65de.jpg'
                    imageClass={styles.imageOfRadioButton}
                    initialValue={selectedType}
                    selectedValue={false}
                    onChangeValue={(value: boolean) => setSelectedType(value)} />
                <RadioButtonField text="Story building"
                    imageUrl='https://storables.com/wp-content/uploads/2023/11/how-tall-is-a-3-story-apartment-building-1700108112.jpg'
                    imageClass={styles.imageOfRadioButton}
                    initialValue={selectedType}
                    selectedValue={true}
                    onChangeValue={(value: boolean) => setSelectedType(value)} />
            </CommonFormFieldWrapper>
            <div style={
                {
                    display: selectedType ? 'block' : 'none',
                }
            }>
                <CommonFormFieldWrapper
                    title="Cost Per Floor"
                    required
                    errors={getFormInputErrors(errors.costPerFloor?.message)}>
                    <Controller
                        render={({ field }) => (
                            <div className={styles.priceInput}>
                                <div className={styles.currency}>
                                    ₦
                                </div>
                                <NumericFormat
                                    placeholder='E.g "15,000"'
                                    allowNegative={false}
                                    allowLeadingZeros={false}
                                    thousandSeparator={true}
                                    onValueChange={(values) => {
                                        field.onChange(values.value)
                                    }}
                                    onBlur={field.onBlur}
                                    value={field.value}
                                />
                            </div>

                        )}
                        name='costPerFloor'
                        control={control}
                        rules={{
                            required: 'Cost per floor is required',
                            validate: (value) => {
                                if(Number.parseFloat(value) <= 0) {
                                    return 'Cost per floor cannot be less than 0'
                                }
                                return true
                            }
                        }}
                    />
                </CommonFormFieldWrapper>
                <span className='spacer' />
            </div>
            <div style={
                {
                    display: selectedType ? 'none' : 'block',
                }
            }>
                <CommonFormFieldWrapper
                    title="Cost For Bungalow"
                    required={true}
                    errors={getFormInputErrors(errors.costForBungalow?.message)}>
                    <Controller
                        render={({ field }) => (
                            <div className={styles.priceInput} >
                                <div className={styles.currency}>
                                    ₦
                                </div>
                                <NumericFormat
                                    placeholder='E.g "15,000"'
                                    allowNegative={false}
                                    allowLeadingZeros={true}
                                    thousandSeparator={true}
                                    onValueChange={(values) => {
                                        field.onChange(values.value)
                                    }}
                                    onBlur={field.onBlur}
                                    value={field.value}
                                />
                            </div>

                        )}
                        name='costForBungalow'
                        control={control}
                        rules={{
                            required: 'Cost for bungalow is required',
                            validate: (value) => {
                                if (Number.parseFloat(value) <= 0) {
                                    return 'Cost per bungalow cannot be less than 0'
                                }
                                return true
                            }
                        }}
                    />
                </CommonFormFieldWrapper>
                <span className='spacer' />
            </div>
            <CommonFormFieldWrapper
                title="Product Images"
                required={true}
                errors={getFormInputErrors(errors.images?.message)}>
                <div className={styles.fileInputContainer}>
                    <CommonFileUploadInput
                        maxFileSize={24}
                        multiple
                        accept='image/*'
                        registerInputProps={register('images', { required: 'Product Images are required' })}
                        onFilesSelect={onFilesSelection}
                        initialFiles={watch('imageNames')}
                    />
                </div>
            </CommonFormFieldWrapper>
            <span className='spacer' />
        </ProductFormWrapper>
    )
})

export default ThirdStepOfCreateProduct
